import {t, Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {useLocalize} from '../../../../../../srcCommon/internationalization/internationalizationContext';
import {OauthClientModel} from '../../../../../models/api/oauthClientModel';

/*
 * Props.
 */

interface DevelopersApplicationsOAuthRedirectUrlsProps {
  oAuthClient: OauthClientModel;
  onUrlsValidationChange: HandlerOf<boolean>;
}

/*
 * Style.
 */

const StyledRedirectUrlsWrapperDiv = styled.div`
  margin-top: 18px;
`;

const StyledTextBoxWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  margin-top: -4px;
  gap: 8px;
`;

const StyledTextBox = styled(TextBox)`
  flex: 1;
`;

const StyledButtonContentWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${(p) => p.theme.palette.blue.shade40};
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 6px;
`;

const StyledIconButton = styled(IconButton)`
  margin-top: 14px;
`;

/*
 * Component.
 */

export const DevelopersApplicationsOAuthRedirectUrls: FC<DevelopersApplicationsOAuthRedirectUrlsProps> = (
  props,
) => {
  const {oAuthClient, onUrlsValidationChange} = props;
  const [redirectUrls, setRedirectUrls] = useState(oAuthClient.redirectUrls);
  const localize = useLocalize();

  useEffect(() => {
    const hasInvalidUrl = redirectUrls.some((url) => computeHasError(url) || url === '');
    onUrlsValidationChange(!hasInvalidUrl);
  }, [redirectUrls, onUrlsValidationChange]);

  const onAddNewUrlClick = () => {
    setRedirectUrls((urls) => [...urls, '']);
  };

  const onRemoveUrlClick = (index: number) => {
    setRedirectUrls((urls) => [...urls.slice(0, index), ...urls.slice(index + 1)]);
  };

  const updateUrlValue = (updatedUrl: string, updatedIndex: number) => {
    setRedirectUrls((urls) =>
      [...urls].map((url, index) => {
        if (index === updatedIndex) {
          return updatedUrl;
        }
        return url;
      }),
    );
  };

  return (
    <StyledRedirectUrlsWrapperDiv>
      {redirectUrls.map((url, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <StyledTextBoxWrapperDiv key={`url-${index}`}>
          <StyledTextBox
            value={url}
            onChange={(updatedUrl) => updateUrlValue(updatedUrl, index)}
            placeholder={localize(
              t(
                'settings-developers-oauth-client-redirect-url-placeholder',
              )`ie. https://example.com/example_example`,
            )}
            hasError={computeHasError(url)}
            errorMessage={renderErrorMessage(url)}
          />
          {redirectUrls.length > 1 && (
            <StyledIconButton iconName="closeSmall" label={null} onClick={() => onRemoveUrlClick(index)} />
          )}
        </StyledTextBoxWrapperDiv>
      ))}
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={onAddNewUrlClick}>
          <StyledButtonContentWrapperDiv>
            <Icon name="plusCircleFilledSmall" />
            <Trans id="settings-developers-oauth-client-add-redirect-url">Add Redirect URL</Trans>
          </StyledButtonContentWrapperDiv>
        </Button>
      </StyledButtonWrapperDiv>
    </StyledRedirectUrlsWrapperDiv>
  );
};

/*
 * Helpers.
 */

function isUrlValid(path: string) {
  try {
    const url = new URL(path);
    return url;
  } catch {
    return undefined;
  }
}

function computeHasError(path: string) {
  const validUrl = isUrlValid(path);

  if (path === '') {
    return false;
  }
  if (!validUrl) {
    return true;
  }
  if (validUrl.protocol !== 'https:') {
    return true;
  }
  return false;
}

function renderErrorMessage(path: string) {
  const validUrl = isUrlValid(path);

  if (!validUrl) {
    return <Trans id="settings-developers-oauth-client-redirect-url-invalid-url">Invalid URL</Trans>;
  }
  if (validUrl.protocol !== 'https:') {
    return (
      <Trans id="settings-developers-oauth-client-redirect-url-requires-https">
        The url must be an https endpoint.
      </Trans>
    );
  }
  return null;
}
